import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { clearAuth } from "slices/auth";
import authEndpoints from "api/endpoints/auth";
import fileEndpoints from "api/endpoints/file";
import siteEndpoints from "api/endpoints/site";
import practiceEndpoints from "api/endpoints/practices";
import reasonEndpoints from "api/endpoints/reasons";
import registerEndpoints from "api/endpoints/register";
import speciesEndpoints from "api/endpoints/species";
import userEndpoints from "api/endpoints/users";
import practitionersEndpoints from "./endpoints/practitioner";
import practiceFeaturesEndpoints from "./endpoints/practice_feature";
import practiceThemesEndpoints from "./endpoints/practice_theme";
import scheduleEndpoints from "./endpoints/schedule";
import breedEndpoints from "./endpoints/breed";
import taskListEndpoints from "./endpoints/task_list";

type DownloadQueryProps = {
  url: string;
  params?: { [key: string]: any };
  filename: string;
};

const baseUrl = (() => {
  if (process.env.NODE_ENV === "production") {
    return process.env.REACT_APP_API_BASE_URL!;
  }
  const url = new URL(process.env.REACT_APP_API_BASE_URL!);
  url.hostname = window.location.hostname;
  return url.toString();
})();

const prepareHeaders = (headers: Headers) => {
  const match = document.cookie.match(
    new RegExp("(^|;\\s*)(XSRF-TOKEN)=([^;]*)"),
  );
  if (match) {
    headers.set("X-XSRF-TOKEN", decodeURIComponent(match[3]));
  }

  return headers;
};

export const downloadQuery = async ({
  url,
  params,
  filename,
}: DownloadQueryProps) => {
  const searchParams = new URLSearchParams(params);
  const urlParams = searchParams.toString();
  const response = await fetch(
    `${baseUrl}${url}${urlParams ? `?${urlParams}` : ""}`,
    {
      credentials: "include",
      headers: prepareHeaders(new Headers()),
    },
  );
  const blob = await response.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
};

const baseQuery = fetchBaseQuery({
  baseUrl,
  credentials: "include",
  prepareHeaders,
});

const baseQueryIntercep: typeof baseQuery = async (args, api, extraOptions) => {
  const queryResult = await baseQuery(args, api, extraOptions);
  if (
    queryResult.error &&
    queryResult.error?.status === 401 &&
    api.endpoint !== "getMe"
  ) {
    api.dispatch(clearAuth());
  }

  return queryResult;
};

const dataApi = createApi({
  reducerPath: "data",
  tagTypes: [
    "AuthMe",

    "Practice",
    "Practices",

    "PracticeTheme",

    "PracticeFeatures",

    "Reason",
    "Reasons",

    "SiteSchedule",

    "Practitioner",
    "Practitioners",

    "User",
    "Users",

    "Breed",
    "Breeds",
  ],
  baseQuery: baseQueryIntercep,
  endpoints: (builder) => ({
    ...authEndpoints(builder),
    ...registerEndpoints(builder),
    ...userEndpoints(builder),
    ...practiceEndpoints(builder),
    ...speciesEndpoints(builder),
    ...breedEndpoints(builder),
    ...fileEndpoints(builder),
    ...reasonEndpoints(builder),
    ...siteEndpoints(builder),
    ...scheduleEndpoints(builder),
    ...speciesEndpoints(builder),
    ...practitionersEndpoints(builder),
    ...practiceFeaturesEndpoints(builder),
    ...practiceThemesEndpoints(builder),
    ...taskListEndpoints(builder),
  }),
});

export default dataApi;

export const {
  // Auth
  useGetMeQuery,
  useGetHeartbeatQuery,
  useLoginMutation,
  useLogoutMutation,
  useVerifyPasswordMutation,
  useForgotPasswordMutation,
  useUpdatePasswordMutation,
  useVerifyAccountMutation,
  useResendVerifyMutation,

  // Register
  useCheckForMatchesMutation,
  useRegisterMutation,
  useSendCodeMutation,
  useVerifyCodeMutation,
  useVerifyPatientMutation,
  useSetPasswordMutation,

  // Practices
  useGetPracticeQuery,
  useGetPracticesQuery,
  useGetPracticeStatsQuery,
  useCreatePracticeMutation,
  useUpdatePracticeMutation,
  useDeletePracticeMutation,
  useVerifyTenantMutation,
  useSwitchPracticeMutation,
  useEndSwitchPracticeMutation,

  // Practice themes
  useUpdatePracticeThemeMutation,

  // Practice features
  useGetPracticeFeaturesQuery,
  useUpdatePracticeFeaturesMutation,

  // Species
  useGetSingleSpeciesQuery,
  useGetSpeciesQuery,
  useUpdateSingleSpeciesMutation,

  // Breeds
  useGetBreedQuery,
  useGetBreedsQuery,
  useUpdateBreedMutation,

  // Files
  useUploadFileMutation,
  useDeleteFileMutation,

  // Reasons
  useGetReasonsQuery,
  useGetReasonQuery,
  useUpdateReasonMutation,

  // Sites (locations)
  useGetSitesQuery,

  // Schedule
  useGetSiteScheduleQuery,
  useUpdateSiteScheduleMutation,

  // Practitioners
  useGetPractitionerQuery,
  useGetPractitionersQuery,
  useUpdatePractitionerMutation,

  // Users
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,

  // Task Lists
  useGetTaskListsQuery,
} = dataApi;
